import type {
  Employee,
  EmployeeComplianceFlags,
  EmployeeDocumentRecord,
  EmployeeMfaProfile,
  EmployeePortfolio,
  EmployeeVoidCheque,
  QualificationStatus,
} from '../../types/employee';
import {
  createDefaultCompliance,
  createDefaultDocuments,
  createDefaultMfaProfile,
  createDefaultPortfolio,
  syncComplianceFromDocuments,
} from '../../types/employee';
import { loadEmployees, saveEmployees } from '../employeeStorage';
import { hashPin, isValidPin } from '../pinService';
import { getActiveCentreId } from '../tenant/centreStorage';

export interface CreateEmployeeInput {
  firstName: string;
  lastName: string;
  email?: string;
  /** Optional on create — punch PIN can be assigned later. */
  pin?: string;
  status?: Employee['status'];
  hireDate?: string;
  mfa?: Partial<EmployeeMfaProfile>;
  qualification?: QualificationStatus;
  echelonCode?: string | null;
  ancienneteHours?: number;
  baseRateDisplay?: number | null;
  voidCheque?: EmployeeVoidCheque | null;
}

export interface UpdateEmployeeInput extends Partial<CreateEmployeeInput> {
  id: string;
  compliance?: Partial<EmployeeComplianceFlags>;
  portfolio?: Partial<EmployeePortfolio>;
  documents?: EmployeeDocumentRecord[];
  /** Pass `null` to clear. */
  voidCheque?: EmployeeVoidCheque | null;
  inviteSent?: boolean;
}

function nextEmployeeId(existing: Employee[]): string {
  const nums = existing
    .map((e) => /^teacher-(\d+)$/.exec(e.id)?.[1])
    .filter(Boolean)
    .map((n) => Number(n));
  const max = nums.length > 0 ? Math.max(...nums) : 0;
  return `teacher-${max + 1}`;
}

export function listEmployees(): Employee[] {
  return loadEmployees();
}

export function getEmployeeById(id: string): Employee | null {
  return loadEmployees().find((e) => e.id === id) ?? null;
}

export function createEmployee(input: CreateEmployeeInput): Employee {
  if (input.pin != null && input.pin !== '' && !isValidPin(input.pin)) {
    throw new Error('invalid-pin');
  }
  const employees = loadEmployees();
  const employee: Employee = {
    id: nextEmployeeId(employees),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: input.email?.trim() || undefined,
    status: input.status ?? 'active',
    pinHash: input.pin && isValidPin(input.pin) ? hashPin(input.pin) : '',
    hireDate: input.hireDate ?? new Date().toISOString().slice(0, 10),
    mfa: createDefaultMfaProfile({
      qualification: input.qualification ?? input.mfa?.qualification ?? 'qualified',
      echelonCode: input.echelonCode ?? input.mfa?.echelonCode ?? null,
      ancienneteHours: input.ancienneteHours ?? input.mfa?.ancienneteHours ?? 0,
      baseRateDisplay: input.baseRateDisplay ?? input.mfa?.baseRateDisplay ?? null,
      ...input.mfa,
    }),
    compliance: createDefaultCompliance(),
    portfolio: createDefaultPortfolio(),
    documents: createDefaultDocuments(),
    voidCheque: input.voidCheque ?? undefined,
    centreId: getActiveCentreId(),
  };
  saveEmployees([...employees, employee]);
  return employee;
}

export function updateEmployee(input: UpdateEmployeeInput): Employee {
  const employees = loadEmployees();
  const index = employees.findIndex((e) => e.id === input.id);
  if (index < 0) throw new Error('not-found');

  const current = employees[index]!;
  const updated: Employee = {
    ...current,
    firstName: input.firstName?.trim() ?? current.firstName,
    lastName: input.lastName?.trim() ?? current.lastName,
    email: input.email !== undefined ? input.email.trim() || undefined : current.email,
    status: input.status ?? current.status,
    hireDate: input.hireDate ?? current.hireDate,
    pinHash:
      input.pin && isValidPin(input.pin) ? hashPin(input.pin) : current.pinHash,
    mfa: {
      ...current.mfa,
      ...(input.mfa ?? {}),
      qualification:
        input.qualification ?? input.mfa?.qualification ?? current.mfa.qualification,
      echelonCode:
        input.echelonCode !== undefined
          ? input.echelonCode
          : input.mfa?.echelonCode ?? current.mfa.echelonCode,
      ancienneteHours:
        input.ancienneteHours ?? input.mfa?.ancienneteHours ?? current.mfa.ancienneteHours,
      baseRateDisplay:
        input.baseRateDisplay !== undefined
          ? input.baseRateDisplay
          : input.mfa?.baseRateDisplay ?? current.mfa.baseRateDisplay,
    },
    compliance: input.documents
      ? syncComplianceFromDocuments(input.documents)
      : input.compliance
        ? { ...current.compliance, ...input.compliance }
        : current.compliance,
    portfolio: input.portfolio
      ? { ...current.portfolio, ...input.portfolio }
      : current.portfolio,
    documents: input.documents ?? current.documents,
    voidCheque:
      input.voidCheque === undefined
        ? current.voidCheque
        : input.voidCheque === null
          ? undefined
          : input.voidCheque,
    inviteSent: input.inviteSent !== undefined ? input.inviteSent : current.inviteSent,
  };

  const next = [...employees];
  next[index] = updated;
  saveEmployees(next);
  return updated;
}

export function addAccumulatedHours(employeeId: string, hours: number): void {
  if (hours <= 0) return;
  const employees = loadEmployees();
  const index = employees.findIndex((e) => e.id === employeeId);
  if (index < 0) return;
  const emp = employees[index]!;
  employees[index] = {
    ...emp,
    mfa: {
      ...emp.mfa,
      accumulatedHours: Math.round((emp.mfa.accumulatedHours + hours) * 10) / 10,
    },
  };
  saveEmployees(employees);
}

export function subtractAccumulatedHours(employeeId: string, hours: number): void {
  if (hours <= 0) return;
  const employees = loadEmployees();
  const index = employees.findIndex((e) => e.id === employeeId);
  if (index < 0) return;
  const emp = employees[index]!;
  employees[index] = {
    ...emp,
    mfa: {
      ...emp.mfa,
      accumulatedHours: Math.max(
        0,
        Math.round((emp.mfa.accumulatedHours - hours) * 10) / 10
      ),
    },
  };
  saveEmployees(employees);
}

export function sumSheetApprovedHours(
  dayRows: { calculatedHours: number | null }[]
): number {
  return dayRows.reduce((sum, row) => sum + (row.calculatedHours ?? 0), 0);
}
